import React from "react";
import { CohortFunnel, DisputeRate, HealthData } from "./types";

export const FUNNEL_LABELS: { key: keyof CohortFunnel; label: string }[] = [
  { key: "signedUp", label: "Signed up" },
  { key: "emailVerified", label: "Verified email" },
  { key: "stripeConnected", label: "Bank connected" },
  { key: "activated", label: "Approved" },
  { key: "firstPaidRequest", label: "First paid request" },
];

export function FunnelBars({ funnel, biggestDrop }: { funnel: CohortFunnel; biggestDrop: HealthData["biggestDrop"] }) {
  const maxCount = Math.max(1, funnel.signedUp);
  return (
    <div style={{ display: "grid", gap: 6 }}>
      {FUNNEL_LABELS.map(({ key, label }, i) => {
        const value = funnel[key];
        const share = funnel.signedUp ? Math.round((value / funnel.signedUp) * 100) : 0;
        const isDropTarget = biggestDrop && biggestDrop.to === key;
        return (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="adm-fs-78" style={{ width: 150, textAlign: "right" }}>{label}</span>
            <div className="adm-flex-1" style={{ height: 18, background: "var(--adm-bg)", borderRadius: 4 }}>
              <div
                style={{
                  width: `${Math.round((value / maxCount) * 100)}%`,
                  minWidth: value > 0 ? 4 : 0,
                  height: "100%",
                  background: "#3b82f6",
                  borderRadius: 4,
                  opacity: 1 - i * 0.12,
                }}
                title={`${label}: ${value} (${share}% of signups)`}
              />
            </div>
            <span className="adm-fs-78 adm-nowrap" style={{ width: 90 }}>
              {value} <span className="muted-text">({share}%)</span>
            </span>
            {isDropTarget && (
              <span
                className="adm-fs-70 adm-bold adm-red adm-nowrap"
              >
                ← biggest drop (−{biggestDrop!.lost})
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// The one number the card networks grade us on. Bar runs to 2× the red line
// so a healthy green reads visibly small; amber/red ticks are the env-tuned
// thresholds the server judged against.
export function DisputeRateGauge({ rate }: { rate: DisputeRate }) {
  const color =
    rate.level === "red"
      ? "var(--adm-red)"
      : rate.level === "amber"
      ? "#b45309"
      : rate.level === "green"
      ? "var(--adm-green)"
      : "var(--adm-text-sec)";
  const label = rate.level === "none" ? "NO DATA" : rate.level.toUpperCase();
  const scaleMax = Math.max(rate.redPct * 2, 0.1);
  const pctOf = (v: number) => `${Math.min(100, (v / scaleMax) * 100)}%`;
  return (
    <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ minWidth: 140 }}>
        <div style={{ fontSize: "2rem", fontWeight: 800, color, lineHeight: 1 }}>
          {rate.ratePct === null ? "—" : `${rate.ratePct}%`}
        </div>
        <span
          style={{
            display: "inline-block",
            marginTop: 6,
            padding: "2px 9px",
            borderRadius: 999,
            fontSize: "0.7rem",
            fontWeight: 700,
            color: "#fff",
            background: color,
          }}
        >
          {label}
        </span>
      </div>
      <div className="adm-flex-1" style={{ minWidth: 220 }}>
        <div style={{ position: "relative", height: 10, borderRadius: 5, background: "rgba(139,148,158,.18)" }}>
          <div
            style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: pctOf(rate.ratePct || 0), borderRadius: 5, background: color }}
          />
          <div
            title={`amber ${rate.amberPct}%`}
            style={{ position: "absolute", top: -3, bottom: -3, left: pctOf(rate.amberPct), width: 2, background: "#b45309" }}
          />
          <div
            title={`red ${rate.redPct}%`}
            style={{ position: "absolute", top: -3, bottom: -3, left: pctOf(rate.redPct), width: 2, background: "var(--adm-red)" }}
          />
        </div>
        <div className="muted-text adm-fs-74 adm-mt-8">
          {rate.disputes} chargeback{rate.disputes === 1 ? "" : "s"} of {rate.paidOrders} paid request
          {rate.paidOrders === 1 ? "" : "s"} in the last {rate.windowDays} days
          {rate.level === "none" ? " — no paid requests in the window yet" : ""}. Per-talent tripwires: chargeback #1
          alerts the founder, #2 auto-pauses the page.
        </div>
      </div>
    </div>
  );
}
